import { Box, Button, Card, Divider, Grid, Stack, TextField, Typography, IconButton, RadioGroup, FormControlLabel, Radio, FormControl, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { DynamicFilterNew } from './DynamicFilterNew';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import WarningIcon from '@mui/icons-material/Warning';
import DownloadIcon from '@mui/icons-material/Download';
import React, { useState, useRef, useEffect } from 'react';
import { getProyectosEmpresa } from '../../../../services/proyectoService';
import { vendedorService } from '../../../../services/vendedorService';

const PLANTILLA_IMPORTACION_ENDPOINT = '/api/prospectos/plantilla';
const PLANTILLA_IMPORTACION_NOMBRE = 'plantilla_importacion.xlsx';

export const PasoUno = ({ value, setValue, filter, setFilter, errors, handleNext, setErrors, initialData = null, hideContinueButton = false, campaignId = null  }) => {
  // Inicializar estados con datos iniciales si existen
  const [excelFile, setExcelFile] = useState(null);
  const [audienceSource, setAudienceSource] = useState(initialData?.upload_audiencia ? 'excel' : 'all'); // 'all' o 'excel'
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [sendType, setSendType] = useState(initialData?.sendType || 'after_approval'); // 'after_approval' o 'specific_date'
  const [sendDate, setSendDate] = useState(initialData?.sendDate || '');
  const [sendTime, setSendTime] = useState(initialData?.sendTime || '');
  const [importProjects, setImportProjects] = useState([]);
  const [importProjectsLoading, setImportProjectsLoading] = useState(false);
  const [importAsesores, setImportAsesores] = useState([]);
  const [importAsesoresLoading, setImportAsesoresLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Si hay datos iniciales, inicializar el valor y filtros
  useEffect(() => {
    if (initialData) {
      if (initialData.nombre_campaign && !value) {
        setValue(initialData.nombre_campaign);
      }
      if (initialData.filtros && filter.length === 0) {
        // Convertir filtros del formato del backend al formato del frontend
        const transformedFilters = initialData.filtros.map(f => ({
          type: f.nombre || f.tipo,
          values: f.valores || []
        }));
        setFilter(transformedFilters);
      }
    }
  }, [initialData]);

  useEffect(() => {
    LoadImportProjects();
    LoadImportAsesores();
  }, []);
  
  const onNext = () => {
    // Validar si seleccionó Excel pero no subió archivo
    if (audienceSource === 'excel' && !excelFile) {
      if (setErrors) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          excel: 'Debes subir un archivo Excel para continuar con audiencia importada.',
        }));
      }
      return;
    }

    // Limpiar error de Excel si ya subió el archivo
    if (setErrors && excelFile) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors.excel;
        return newErrors;
      });
    }

    // Si no hay filtros, mostrar diálogo de confirmación
    if (filter.length === 0) {
      setShowConfirmDialog(true);
      return;
    }
    handleNext(value, filter, excelFile, audienceSource, sendType, sendDate, sendTime, '', '');
  };

  const handleConfirmContinue = () => {
    setShowConfirmDialog(false);
    // Limpiar el error de filtros antes de continuar
    if (setErrors) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors.filter;
        return newErrors;
      });
    }
    handleNext(value, filter, excelFile, audienceSource, sendType, sendDate, sendTime, '', '');
  };

  const validateExcelFile = (file) => {
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.split('.').pop();
    const validMimeTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];
    return fileExtension === 'xlsx' && validMimeTypes.includes(file.type);
  };

  const handleExcelFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!validateExcelFile(selectedFile)) {
        alert('Tipo de archivo no válido. Por favor selecciona un archivo Excel (.xlsx)');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      setExcelFile(selectedFile);
      // Limpiar error de Excel cuando se sube un archivo válido
      if (setErrors) {
        setErrors((prevErrors) => {
          const newErrors = { ...prevErrors };
          delete newErrors.excel;
          return newErrors;
        });
      }
      // Si estamos en modo de edición, sincronizar inmediatamente con el padre
      if (hideContinueButton && handleNext) {
        handleNext(value, filter, selectedFile, audienceSource, sendType, sendDate, sendTime, '', '');
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (!validateExcelFile(droppedFile)) {
        alert('Tipo de archivo no válido. Por favor selecciona un archivo Excel (.xlsx)');
        return;
      }
      setExcelFile(droppedFile);
      // Limpiar error de Excel cuando se sube un archivo válido
      if (setErrors) {
        setErrors((prevErrors) => {
          const newErrors = { ...prevErrors };
          delete newErrors.excel;
          return newErrors;
        });
      }
      // Si estamos en modo de edición, sincronizar inmediatamente con el padre
      if (hideContinueButton && handleNext) {
        handleNext(value, filter, droppedFile, audienceSource, sendType, sendDate, sendTime, '', '');
      }
    }
  };

  const handleRemoveExcelFile = () => {
    setExcelFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    // Si estamos en modo de edición, sincronizar inmediatamente con el padre
    if (hideContinueButton && handleNext) {
      handleNext(value, filter, null, audienceSource, sendType, sendDate, sendTime, '', '');
    }
  };

  const handleAudienceSourceChange = (event) => {
    const newSource = event.target.value;
    setAudienceSource(newSource);
    // Si cambia a 'all', limpiar el archivo Excel
    if (newSource === 'all') {
      setExcelFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
    // Si estamos en modo de edición, sincronizar inmediatamente con el padre
    if (hideContinueButton && handleNext) {
      handleNext(value, filter, newSource === 'all' ? null : excelFile, newSource, sendType, sendDate, sendTime, '', '');
    }
  };

  const LoadImportProjects = async () => {
    setImportProjectsLoading(true);
    try {
      const projects = await getProyectosEmpresa();
      setImportProjects(Array.isArray(projects) ? projects : []);
    } catch (error) {
      console.error('❌ Error cargando proyectos para importación:', error);
      setImportProjects([]);
    } finally {
      setImportProjectsLoading(false);
    }
  };

  const LoadImportAsesores = async () => {
    setImportAsesoresLoading(true);
    try {
      const asesores = await vendedorService.getAsesoresParaImportacion();
      setImportAsesores(Array.isArray(asesores) ? asesores : []);
    } catch (error) {
      console.error('❌ Error cargando asesores para importación:', error);
      setImportAsesores([]);
    } finally {
      setImportAsesoresLoading(false);
    }
  };

  const GetProyectoImportacionInfo = (project) => {
    const id = project?.codigoProyecto || project?.codigo_proyecto || project?.id;
    const nombre = project?.nombreProyecto || project?.nombre_proyecto || project?.nombre || `Proyecto ${id}`;

    return { id, nombre };
  };

  const GetAsesorImportacionInfo = (asesor) => {
    const nombre = [asesor?.nombre, asesor?.apellido].filter(Boolean).join(' ') || asesor?.email || 'Asesor sin nombre';

    return {
      id: asesor?.id,
      nombre,
      estado: asesor?.estado || 'activo',
    };
  };

  const handleDownloadFormat = async () => {
      try {
          const token = localStorage.getItem('token');
          const response = await fetch(PLANTILLA_IMPORTACION_ENDPOINT, {
            method: 'GET',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });

          if (!response.ok) {
            throw new Error('No se pudo descargar la plantilla.');
          }

          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = PLANTILLA_IMPORTACION_NOMBRE;
          link.style.display = 'none';

          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
      } catch (error) {
          console.error('❌ Error al descargar formato:', error);
          alert('Error al descargar el formato Excel');
      }
  };

  // Función segura para descargar con Token
  const handleSecureDownload = async () => {
    if (!campaignId) return;

    try {
      const token = localStorage.getItem('token'); // Asegúrate que el token se guarda aquí
      
      const response = await fetch(`/api/campania/${campaignId}/descargar-audiencia`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}` // Aquí enviamos el token
        }
      });

      if (!response.ok) {
        if(response.status === 404) throw new Error("No hay archivo de audiencia cargado.");
        if(response.status === 403) throw new Error("No tienes permisos.");
        throw new Error("Error en la descarga");
      }

      // Convertir la respuesta a un Blob (archivo binario)
      const blob = await response.blob();
      
      // Crear URL temporal y forzar descarga
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audiencia_campania_${campaignId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      
      // Limpieza
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error("Error descarga:", err);
      alert("Error al descargar: " + err.message);
    }
  };

  return (
    <Card
      sx={{
        display: 'flex',
        flex: '1 1 auto',
        backgroundColor: '#FFFFFF',
        flexDirection: 'column',
        p: 3,
      }}>
      <Typography sx={{ fontWeight: 'bold', fontSize: 20, color: '#031b4e', mb: 1 }}>
        Datos de campañas
      </Typography>
      <Typography sx={{ color: '#6B7280', fontSize: 14, mb: 3 }}>
        En esta sección podrás personalizar tu campaña según tus necesidades.
      </Typography>

      <Divider sx={{ borderColor: '#E5E7EB', mb: 3, width: '100%' }} />

      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 1 }}>
          Nombre de campaña
        </Typography>
        <TextField
          placeholder="Escribe el nombre de tu campaña"
          variant="outlined"
          size="small"
          fullWidth
          value={value}
          onChange={(e) => setValue(e.target.value)}
          error={!!errors.value}
          helperText={errors.value}
          InputProps={{
            style: { fontSize: '14px' },
          }}
        />
      </Box>

      <Typography sx={{ fontSize: 18, fontWeight: 'bold', color: '#031b4e', mt: 4, mb: 2 }}>
        Selección de audiencia
      </Typography>
      <Typography sx={{ color: '#6B7280', fontSize: 14, mb: 3 }}>
        Define y segmenta la audiencia objetivo para maximizar el impacto de la campaña.
      </Typography>

      {/* Radio buttons para seleccionar fuente de audiencia */}
      <FormControl component="fieldset" sx={{ mb: 3 }}>
        <RadioGroup
          value={audienceSource}
          onChange={handleAudienceSourceChange}
          sx={{ gap: 1 }}
        >
          <FormControlLabel
            value="all"
            control={<Radio />}
            label={
              <Typography sx={{ fontSize: 14, color: '#222' }}>
                Audiencia de la base de datos
              </Typography>
            }
          />
          <FormControlLabel
            value="excel"
            control={<Radio />}
            label={
              <Typography sx={{ fontSize: 14, color: '#222' }}>
                Audiencia importada desde Excel
              </Typography>
            }
          />
        </RadioGroup>
      </FormControl>

      {/* Upload de Excel - Solo se muestra si se selecciona la opción Excel */}
      {audienceSource === 'excel' && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ color: '#6B7280', fontSize: 12, mb: 1 }}>
            Sube un archivo Excel (.xlsx) con la lista de contactos para tu campaña
          </Typography>
          
          <Box sx={{ mb: 2, display: 'flex', gap: 3 }}>
            <Box 
              component="a"
              onClick={handleDownloadFormat}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                color: '#3451a1',
                fontSize: 12,
                textDecoration: 'underline',
                cursor: 'pointer',
                '&:hover': {
                  color: '#5a7aef',
                },
              }}
            >
              <DownloadIcon sx={{ fontSize: 16 }} />
              <Typography component="span" sx={{ fontSize: 12, fontWeight: 600 }}>
                Descargar plantilla con `project_id` y `asesor_id`
              </Typography>
            </Box>

            {/* BOTÓN DESCARGAR AUDIENCIA CON AUTH */}
            {campaignId && (
              <Box 
                component="a"
                onClick={handleSecureDownload} // <--- CAMBIO AQUÍ
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: '#3451a1',
                  fontSize: 12,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontWeight: 600,
                  '&:hover': {
                    color: '#5a7aef',
                  },
                }}
              >
                <DownloadIcon sx={{ fontSize: 16 }} />
                <Typography component="span" sx={{ fontSize: 12, fontWeight: 600 }}>
                  Descargar audiencia actual
                </Typography>
              </Box>
            )}
          </Box>

          <Alert severity="info" sx={{ mb: 2, alignItems: 'flex-start' }}>
            <Typography sx={{ fontSize: 13, mb: 1 }}>
              Usa las columnas <strong>`project_id`</strong> y <strong>`asesor_id`</strong> en el Excel. Proyecto y asesor ya no se seleccionan en esta pantalla: ambos se toman directamente desde el archivo importado.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#031b4e', mb: 1 }}>
                  Asesores disponibles
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gap: 1,
                    maxHeight: 240,
                    overflowY: 'auto',
                    pr: 1,
                  }}
                >
                  {importAsesoresLoading ? (
                    <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
                      Cargando asesores...
                    </Typography>
                  ) : importAsesores.length > 0 ? (
                    importAsesores.map((asesor) => {
                      const asesorInfo = GetAsesorImportacionInfo(asesor);
                      return (
                        <Box
                          key={asesorInfo.id}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 1,
                            p: 1,
                            borderRadius: 1,
                            backgroundColor: '#F8FAFC',
                          }}
                        >
                          <Typography sx={{ fontSize: 12 }}>
                            {asesorInfo.nombre}
                          </Typography>
                          <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                            ID: {asesorInfo.id} ({asesorInfo.estado})
                          </Typography>
                        </Box>
                      );
                    })
                  ) : (
                    <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
                      No se pudieron cargar los asesores.
                    </Typography>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#031b4e', mb: 1 }}>
                  Proyectos habilitados
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gap: 1,
                    maxHeight: 240,
                    overflowY: 'auto',
                    pr: 1,
                  }}
                >
                  {importProjectsLoading ? (
                    <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
                      Cargando proyectos...
                    </Typography>
                  ) : importProjects.length > 0 ? (
                    importProjects.map((project) => {
                      const projectInfo = GetProyectoImportacionInfo(project);
                      return (
                        <Box
                          key={projectInfo.id}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 1,
                            p: 1,
                            borderRadius: 1,
                            backgroundColor: '#F8FAFC',
                          }}
                        >
                          <Typography sx={{ fontSize: 12 }}>
                            {projectInfo.nombre}
                          </Typography>
                          <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                            ID: {projectInfo.id}
                          </Typography>
                        </Box>
                      );
                    })
                  ) : (
                    <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
                      No se pudieron cargar los proyectos.
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Alert>

          {errors.excel && (
            <Typography sx={{ color: 'red', fontSize: 13, mb: 2 }}>
              {errors.excel}
            </Typography>
          )}
          {!excelFile ? (
          <Box
            sx={{
              border: '1px dashed #bfc8e6',
              borderRadius: 2,
              p: 3,
              background: '#f7f8fc',
              cursor: 'pointer',
              textAlign: 'center',
            }}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <CloudUploadIcon
              sx={{ fontSize: 40, color: '#bfc8e6', mb: 1 }}
            />
            <Typography sx={{ color: '#8a8fa7', mb: 1, fontSize: 14 }}>
              Arrastra y suelta un archivo Excel para subirlo
            </Typography>
            <Typography
              component="span"
              sx={{
                color: '#3451a1',
                fontSize: 14,
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              O elige un archivo de tu dispositivo
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleExcelFileChange}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: '#fff',
              borderRadius: 2,
              p: 2,
              border: '1px solid #e0e4f7',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InsertDriveFileIcon sx={{ color: '#3451a1' }} />
              <Typography>{excelFile.name}</Typography>
            </Box>
            <IconButton onClick={handleRemoveExcelFile}>
              <DeleteIcon color="error" />
            </IconButton>
          </Box>
          )}
        </Box>
      )}

      <Box
        sx={{
          border: '1px solid #B0B0B0',
          width: '100%',
          borderRadius: '5px',
          p: 2,
        }}>
        <Typography sx={{ fontSize: 14, mb: 1, color: '#B0B0B0' }}>Filtros</Typography>
        {errors.filter && <Typography sx={{ color: 'red', fontSize: 14 }}>{errors.filter}</Typography>}
        {filter.length === 0 && (
          <Alert 
            severity="warning" 
            icon={<WarningIcon />}
            sx={{ mb: 2, fontSize: 13 }}
          >
            No has agregado ningún filtro. La campaña se aplicará a toda la base de datos.
          </Alert>
        )}
        <hr/>
        <DynamicFilterNew filters={filter} setFilters={setFilter} />
      </Box>

      {/* Configuración de envío */}
      <Box sx={{ mt: 4, mb: 3 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 'bold', color: '#031b4e', mb: 2 }}>
          Configuración de envío
        </Typography>
        <Typography sx={{ color: '#6B7280', fontSize: 14, mb: 3 }}>
          Define cuándo se enviará la campaña.
        </Typography>

        <FormControl component="fieldset" sx={{ mb: 3 }}>
          <RadioGroup
            value={sendType}
            onChange={(e) => {
              const newSendType = e.target.value;
              setSendType(newSendType);
              // Si estamos en modo de edición, sincronizar inmediatamente con el padre
              if (hideContinueButton && handleNext) {
                handleNext(value, filter, excelFile, audienceSource, newSendType, sendDate, sendTime, '', '');
              }
            }}
            sx={{ gap: 1 }}
          >
            <FormControlLabel
              value="after_approval"
              control={<Radio />}
              label={
                <Typography sx={{ fontSize: 14, color: '#222' }}>
                  Envío después de la aprobación de la plantilla
                </Typography>
              }
            />
            <FormControlLabel
              value="specific_date"
              control={<Radio />}
              label={
                <Typography sx={{ fontSize: 14, color: '#222' }}>
                  Envío en una fecha específica
                </Typography>
              }
            />
          </RadioGroup>
        </FormControl>

        {/* Inputs de fecha y hora - Solo se muestran cuando se selecciona fecha específica */}
        {sendType === 'specific_date' && (
          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
            <Box sx={{ width: '200px' }}>
              <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 1 }}>
                Fecha
              </Typography>
              <TextField
                type="date"
                variant="outlined"
                size="small"
                fullWidth
                value={sendDate}
                onChange={(e) => {
                  const newSendDate = e.target.value;
                  setSendDate(newSendDate);
                  // Si estamos en modo de edición, sincronizar inmediatamente con el padre
                  if (hideContinueButton && handleNext) {
                    handleNext(value, filter, excelFile, audienceSource, sendType, newSendDate, sendTime, '', '');
                  }
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  style: { fontSize: '14px' },
                }}
              />
            </Box>
            <Box sx={{ width: '150px' }}>
              <Typography sx={{ fontWeight: 500, fontSize: 14, mb: 1 }}>
                Hora
              </Typography>
              <TextField
                type="time"
                variant="outlined"
                size="small"
                fullWidth
                value={sendTime}
                onChange={(e) => {
                  const newSendTime = e.target.value;
                  setSendTime(newSendTime);
                  // Si estamos en modo de edición, sincronizar inmediatamente con el padre
                  if (hideContinueButton && handleNext) {
                    handleNext(value, filter, excelFile, audienceSource, sendType, sendDate, newSendTime, '', '');
                  }
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  style: { fontSize: '14px' },
                }}
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Diálogo de confirmación para continuar sin filtros */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon sx={{ color: '#ff9800' }} />
          ¿Continuar sin filtros?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description" sx={{ fontSize: 14 }}>
            No has agregado ningún filtro a tu campaña. Si continúas, la campaña se aplicará a <strong>toda la audiencia</strong>.
            <br /><br />
            ¿Estás seguro de que deseas continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowConfirmDialog(false)}
            sx={{ textTransform: 'none', color: '#6B7280' }}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmContinue}
            variant="contained"
            autoFocus
            sx={{ 
              textTransform: 'none',
              backgroundColor: '#6a8bff',
              '&:hover': {
                backgroundColor: '#5a7aef',
              },
            }}
          >
            Sí, continuar
          </Button>
        </DialogActions>
      </Dialog>

      {!hideContinueButton && (
      <Stack direction='row' justifyContent='flex-end' spacing={2} sx={{ mt: 5, mb: 1 }}>
        <Button
          variant="contained"
          sx={{
            backgroundColor: '#6a8bff',
            color: '#fff',
            fontSize: 14,
            borderRadius: 2,
            boxShadow: 'none',
            textTransform: 'none',
            paddingX: 4,
            paddingY: 1.5,
            fontWeight: 500,
            '&:hover': {
              backgroundColor: '#5a7aef',
              boxShadow: 'none',
            },
          }}
          onClick={onNext}
        >
          Continuar
        </Button>
      </Stack>
      )}
    </Card>
  );
};
